/**
 * Strategic Intelligence Dashboard - Unified Value View
 *
 * Merges the Analytical View (Scores & Metrics) with the Strategic View (Prism & ROI)
 * to eliminate cognitive friction between separate pages.
 *
 * The Triptych of Value:
 * - Zone 1 (Top): Executive Pulse - The "What" (Scores & Risk)
 * - Zone 2 (Middle): The Trust Bridge - The "Why" (Prism Visualization)
 * - Zone 3 (Bottom): Actionable Intelligence - The "How" (Recommendations)
 *
 * Design Philosophy: Move the user from Complexity to Clarity
 *
 * Sub-components extracted to ./strategic-intelligence/ (DC-1367):
 * - AssetMaturityCard, MecePillarWidget, ExecutionRiskCard
 * - ExecutiveSummaryTab, ValueDriversTab, AssessmentValueBanner
 *
 * @version 1.2.0
 * @date 2026-02-14
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import 'react-circular-progressbar/dist/styles.css';
import { IntelligencePrism, PrismData } from './IntelligencePrism';
import { BridgeRecommendations } from './BridgeRecommendations';
import { AlcoaSummaryCard } from './regulatory/AlcoaSummaryCard';
import { ComplianceLensCard } from './lenses';
import { FDACredibilityCard } from './lenses/FDACredibilityCard';
import { CausalInsightsPanel } from './CausalInsightsPanel';
import { LineageHistoryPanel, LineageComparisonPanel } from './lineage';
import { useApiClient } from '../hooks/useApiClient';
import { useVisibilityRecheck } from '../hooks/useVisibilityRecheck';
import { useJourneyId, useJourneyName } from '../contexts/JourneyContext';
import './strategic-intelligence-dashboard.css';
import '../styles/assessment-value-banner.css';

// Extracted sub-components (DC-1367)
import { AssetMaturityCard } from './strategic-intelligence/AssetMaturityCard';
import { MecePillarWidget } from './strategic-intelligence/MecePillarWidget';
import { ExecutionRiskCard } from './strategic-intelligence/ExecutionRiskCard';
import { ExecutiveSummaryTab } from './strategic-intelligence/ExecutiveSummaryTab';
import { ValueDriversTab } from './strategic-intelligence/ValueDriversTab';
import { AssessmentValueBanner } from './strategic-intelligence/AssessmentValueBanner';
import type {
  TechnicalAssetScore,
  ExecutionRiskData,
  BridgeIntelligence,
  ActiveTab,
} from './strategic-intelligence/types';

// Re-export key types for backward compatibility
export type { TechnicalAssetScore, ExecutionRiskData, BridgeIntelligence } from './strategic-intelligence/types';

// ============================================================================
// PROPS
// ============================================================================

interface StrategicIntelligenceDashboardProps {
  journeyId?: string;
  journeyName?: string;
  onStationClick?: (stationId: string) => void;
  onNavigateToCV?: () => void;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const StrategicIntelligenceDashboard: React.FC<StrategicIntelligenceDashboardProps> = ({
  journeyId: journeyIdProp,
  journeyName: journeyNameProp,
  onStationClick,
  onNavigateToCV,
}) => {
  const journeyId = useJourneyId(journeyIdProp);
  const _journeyName = useJourneyName(journeyNameProp);
  const navigate = useNavigate();
  const { authFetch } = useApiClient();

  // State
  const [technicalScore, setTechnicalScore] = useState<TechnicalAssetScore | null>(null);
  const [executionRisk, setExecutionRisk] = useState<ExecutionRiskData | null>(null);
  const [bridgeIntelligence, setBridgeIntelligence] = useState<BridgeIntelligence | null>(null);
  const [prismData, setPrismData] = useState<PrismData | null>(null);
  const [prismRefreshTrigger, setPrismRefreshTrigger] = useState(0);
  const [cvCompleted, setCvCompleted] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('summary');

  // Context modal state
  const [showContextModal, setShowContextModal] = useState(false);
  const [userContext, setUserContext] = useState('');

  // DC-872, DC-873: Lineage panel visibility
  const [showLineageHistory, setShowLineageHistory] = useState(false);
  const [showLineageComparison, setShowLineageComparison] = useState(false);

  const hasInsights = !!(bridgeIntelligence?.recommendations?.length);

  // Callback when IntelligencePrism loads its data - single source of truth
  const handlePrismDataLoaded = useCallback((data: PrismData) => {
    setPrismData(data);
  }, []);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  useEffect(() => {
    fetchAllData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId]);

  // DC-1469: Check if CV has been completed for soft nudge banner
  useEffect(() => {
    if (!journeyId) return;
    const checkCvStatus = async () => {
      try {
        const response = await authFetch(`/api/contextual-validity/journeys/${journeyId}/results`);
        setCvCompleted(response.ok);
      } catch {
        setCvCompleted(false);
      }
    };
    checkCvStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId]);

  const fetchAllData = async () => {
    if (!journeyId) return;

    try {
      setLoading(true);

      const [scoreRes, riskRes, bridgeRes] = await Promise.all([
        authFetch(`/api/journey/${journeyId}/technical-asset-score`),
        authFetch('/api/organization/execution-risk'),
        authFetch(`/api/bridge/${journeyId}/intelligence?check_only=true`),
      ]);

      // Technical Asset Score
      if (scoreRes.ok) {
        const scoreData = await scoreRes.json();
        if (scoreData.success && scoreData.score) {
          setTechnicalScore(scoreData.score);
        }
      }

      // Execution Risk
      if (riskRes.ok) {
        const riskData = await riskRes.json();
        if (riskData.success && riskData.execution_risk) {
          const risk = riskData.execution_risk;
          setExecutionRisk({
            enabled: risk.enabled !== false,
            combined_factor: risk.combined_factor,
            risk_level: risk.risk_level,
            people_readiness_score: risk.people_readiness?.factor != null
              ? risk.people_readiness.factor * 100
              : null,
            governance_maturity_score: risk.governance_maturity?.factor != null
              ? risk.governance_maturity.factor * 100
              : null,
            interop_maturity_score: risk.interoperability_maturity?.factor != null
              ? risk.interoperability_maturity.factor * 100
              : null,
            recommendation: risk.recommendation,
            interview_count: riskData.interview_count || 0,
            document_count: riskData.document_count || 0,
            standards_enabled: riskData.standards_enabled || 0,
            standards_total: riskData.standards_total || 4,
          });
        }
      }

      // Bridge Intelligence (if already generated)
      let bridgeData: BridgeIntelligence | null = null;
      if (bridgeRes.ok) {
        bridgeData = await bridgeRes.json();
      }

      // Fetch saved strategic guidance (Zone 1 AI insights)
      try {
        const guidanceRes = await authFetch(`/api/bridge/${journeyId}/strategic-guidance`);
        if (guidanceRes.ok) {
          const guidanceResult = await guidanceRes.json();
          if (guidanceResult.success && guidanceResult.guidance) {
            bridgeData = {
              ...bridgeData,
              score_improvement_guidance: guidanceResult.guidance.score_improvement_guidance,
              execution_risk_guidance: guidanceResult.guidance.execution_risk_guidance,
            } as BridgeIntelligence;
            console.log('[StrategicDashboard] Loaded saved strategic guidance from database');
          }
        }
      } catch (guidanceErr) {
        console.warn('[StrategicDashboard] Failed to fetch strategic guidance:', guidanceErr);
      }

      if (bridgeData) {
        setBridgeIntelligence(bridgeData);
      }

    } catch (err) {
      console.error('[StrategicDashboard] Error fetching data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // PERSISTED-INSIGHTS RE-CHECK (v2.0 self-heal)
  // ============================================================================
  // Generation runs 1–2 min server-side and persists on completion. If this
  // surface missed the response (unmounted mid-flight, remounted before the
  // run finished, fetch dropped), the empty state would strand until a page
  // reload. A light check_only probe on tab return — and on a slow poll while
  // a generation is in flight — adopts the persisted result: the full mount
  // fetch re-runs (guidance-merge parity) and the Prism is woken via its
  // refresh trigger, mirroring the normal generate-success path.
  const recheckPersistedInsights = async () => {
    if (!journeyId) return;
    try {
      const res = await authFetch(`/api/bridge/${journeyId}/intelligence?check_only=true`);
      if (!res.ok) return; // 404 = genuinely not generated yet
      const data = await res.json();
      if (!data || data.error || !data.executive_summary) return;
      console.log('[StrategicDashboard] Persisted insights found on re-check — adopting');
      await fetchAllData();
      setPrismRefreshTrigger(prev => prev + 1);
    } catch {
      // transient — the next visibility event or poll tick retries
    }
  };

  useVisibilityRecheck(recheckPersistedInsights, !bridgeIntelligence && !loading && !generating);

  // Backstop while a generation is in flight in THIS mount: if the response
  // is lost (connection drop, proxy hiccup) the server still persists — the
  // poll picks the result up instead of leaving the spinner to die into an
  // error the user has to resolve by reloading.
  useEffect(() => {
    if (!generating) return;
    const interval = window.setInterval(recheckPersistedInsights, 20_000);
    return () => window.clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generating]);

  // ============================================================================
  // AI GENERATION
  // ============================================================================

  const handleGenerateClick = () => {
    setShowContextModal(true);
  };

  const handleSynthesizeInsights = async () => {
    setShowContextModal(false);
    setGenerating(true);
    setError(null);

    try {
      console.log('[StrategicDashboard] Starting insight generation...');
      console.log('[StrategicDashboard] technicalScore available:', !!technicalScore);
      console.log('[StrategicDashboard] executionRisk available:', !!executionRisk);

      const [bridgeResponse, guidanceResponse] = await Promise.all([
        authFetch(`/api/bridge/${journeyId}/intelligence/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            context: userContext.trim() || undefined,
          }),
        }),
        authFetch(`/api/bridge/${journeyId}/strategic-guidance`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            technical_score: technicalScore ? {
              overall_score: technicalScore.overall_score,
              rating: technicalScore.rating,
              pillars: {
                statistical_health: { score: technicalScore.pillars?.statistical_health?.score || 0 },
                semantic_maturity: { score: technicalScore.pillars?.semantic_maturity?.score || 0 },
                governance_fair: { score: technicalScore.pillars?.governance_fair?.score || 0 },
              },
            } : undefined,
            execution_risk: executionRisk ? {
              risk_level: executionRisk.risk_level,
              combined_factor: executionRisk.combined_factor,
              people_readiness: {
                factor: (executionRisk.people_readiness_score ?? 0) / 100,
                interview_count: executionRisk.interview_count,
              },
              governance_maturity: {
                factor: (executionRisk.governance_maturity_score ?? 0) / 100,
                document_count: executionRisk.document_count,
              },
              interoperability_maturity: {
                factor: (executionRisk.interop_maturity_score ?? 0) / 100,
                standards_enabled: executionRisk.standards_enabled,
                standards_total: executionRisk.standards_total,
              },
            } : undefined,
          }),
        }),
      ]);

      if (!bridgeResponse.ok) {
        if (bridgeResponse.status === 503) {
          throw new Error('AI insights not available (no AI provider configured)');
        }
        throw new Error('Failed to generate insights');
      }

      const bridgeData = await bridgeResponse.json();

      // DC-1630: On long generations the server commits 200 + chunked headers
      // before the LLM call (to defeat Azure's 230s LB idle timeout), so a
      // post-headers failure arrives as 200 + an `{ error }` body. Surface it.
      if (bridgeData?.error && !bridgeData.executive_summary) {
        throw new Error(typeof bridgeData.error === 'string' ? bridgeData.error : 'Failed to generate insights');
      }

      // Fail-loud: the recommendations synthesis errored (e.g. provider rate
      // limit), so the summary came back but priorities/ROI did not. The server
      // intentionally did NOT persist this, so the journey stays in the
      // "Awaiting AI Synthesis" state. Surface the failure + retry instead of
      // rendering a confident summary atop 0 priorities / $0 ROI.
      if (bridgeData?.synthesis_status === 'failed') {
        throw new Error(
          bridgeData?.synthesis_error?.message ||
            'AI synthesis failed before recommendations could be generated. Please retry.'
        );
      }

      let guidanceData = null;
      if (guidanceResponse && guidanceResponse instanceof Response && guidanceResponse.ok) {
        const response = await guidanceResponse.json();
        if (response.success && response.guidance) {
          guidanceData = response.guidance;
        }
      }

      const mergedIntelligence: BridgeIntelligence = {
        ...bridgeData,
        score_improvement_guidance: guidanceData?.score_improvement_guidance,
        execution_risk_guidance: guidanceData?.execution_risk_guidance,
      };

      setBridgeIntelligence(mergedIntelligence);
      setPrismRefreshTrigger(prev => prev + 1);
      setActiveTab('summary');

    } catch (err) {
      console.error('[StrategicDashboard] Generation error:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate insights');
    } finally {
      setGenerating(false);
    }
  };

  // ============================================================================
  // RENDER: LOADING STATE
  // ============================================================================

  if (loading) {
    return (
      <div className="strategic-dashboard strategic-dashboard--loading">
        <div className="dashboard-loading-container">
          <div className="loading-spinner" />
          <p>Loading Strategic Intelligence...</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: MAIN DASHBOARD
  // ============================================================================

  return (
    <div className="strategic-dashboard">
      {/* Error Banner */}
      {error && (
        <div className="error-banner" role="alert">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* ================================================================
          ZONE 1: EXECUTIVE PANEL - The "What" (Scores & Risk)
          ================================================================ */}
      <section className="zone-executive-panel">
        <div className="zone-header">
          <h2 className="zone-title">Executive Pulse</h2>
          <span className="zone-subtitle">Real-time Asset & Organizational Readiness</span>
        </div>

        <div className="panel-grid">
          <AssetMaturityCard
            score={technicalScore}
            guidance={bridgeIntelligence?.score_improvement_guidance}
          />
          <MecePillarWidget pillars={technicalScore?.pillars} journeyId={journeyId} />
          {/* DC-1646: org-axis metric → compact labeled reference, not a
              re-rendered widget; canonical home is Readiness → Organizational. */}
          <ExecutionRiskCard risk={executionRisk} />
        </div>
      </section>

      {/* ================================================================
          ZONE 1.5: REGULATORY COMPLIANCE - ALCOA+ & Privacy Lenses
          ================================================================ */}
      <section className="zone-regulatory-compliance">
        <div className="zone-header">
          <h2 className="zone-title">Regulatory Compliance</h2>
          <span className="zone-subtitle">Data Integrity & Privacy Assessment</span>
          <div className="regulatory-badges">
            <div className="regulatory-badge">
              <span className="badge-indicator" />
              <span>21 CFR Part 11</span>
            </div>
            <div className="regulatory-badge privacy-badge">
              <span className="badge-indicator" />
              <span>GDPR / HIPAA / CCPA</span>
            </div>
            <div className="regulatory-badge fda-badge">
              <span className="badge-indicator" />
              <span>FDA AI/ML 2025</span>
            </div>
          </div>
        </div>

        <div className="regulatory-content regulatory-content--grid">
          <AlcoaSummaryCard
            journeyId={journeyId}
            onViewDetails={(assessmentId) => {
              navigate(`/journey/${journeyId}/alcoa?assessment=${assessmentId}`);
            }}
            onViewFindings={(severity) => {
              const params = severity ? `?severity=${severity}` : '';
              navigate(`/journey/${journeyId}/alcoa${params}`);
            }}
          />
          <ComplianceLensCard
            journeyId={journeyId}
            onViewDetails={(assessmentId) => {
              navigate(`/journey/${journeyId}/compliance?assessment=${assessmentId}`);
            }}
            onViewFindings={(severity) => {
              const params = severity ? `?severity=${severity}` : '';
              navigate(`/journey/${journeyId}/compliance${params}`);
            }}
          />
          <FDACredibilityCard
            journeyId={journeyId}
            onViewDetails={() => {
              navigate(`/journey/${journeyId}/fda-credibility`);
            }}
          />
        </div>
      </section>

      {/* ================================================================
          ZONE 2: THE TRUST BRIDGE - The "Why" (Prism Visualization)
          ================================================================ */}
      <section className="zone-trust-bridge">
        <div className="zone-header">
          <h2 className="zone-title">The Trust Bridge</h2>
          <span className="zone-subtitle">Evidence Provenance: How Findings Become Value</span>
          <div className="bridge-ai-badge">
            <span className="badge-indicator" />
            <span>Bridge AI</span>
          </div>
        </div>

        <div className="prism-wrapper">
          <IntelligencePrism
            journeyId={journeyId}
            onStationClick={(stationId) => {
              console.log('[StrategicDashboard] Station clicked:', stationId);
              onStationClick?.(stationId);
            }}
            onDriverClick={(driverId) => console.log('Driver clicked:', driverId)}
            onDataLoaded={handlePrismDataLoaded}
            refreshTrigger={prismRefreshTrigger}
          />
        </div>

        {/* DC-872, DC-873: Data Lineage & Provenance Panels */}
        <div className="lineage-controls">
          <button
            className={`lineage-toggle-btn ${showLineageHistory ? 'active' : ''}`}
            onClick={() => setShowLineageHistory(!showLineageHistory)}
          >
            <span className="toggle-icon">📜</span>
            {showLineageHistory ? 'Hide' : 'Show'} Lineage History
          </button>
          <button
            className={`lineage-toggle-btn ${showLineageComparison ? 'active' : ''}`}
            onClick={() => setShowLineageComparison(!showLineageComparison)}
          >
            <span className="toggle-icon">🔄</span>
            {showLineageComparison ? 'Hide' : 'Show'} Version Comparison
          </button>
        </div>

        {showLineageHistory && (
          <div className="lineage-panel-wrapper">
            <LineageHistoryPanel journeyId={journeyId} />
          </div>
        )}

        {showLineageComparison && (
          <div className="lineage-panel-wrapper">
            <LineageComparisonPanel journeyId={journeyId} />
          </div>
        )}

      </section>

      {/* ================================================================
          ZONE 3: ACTIONABLE INTELLIGENCE - The "How" (Recommendations)
          ================================================================ */}
      {/* DC-1469: CV Soft Nudge Banner. Once CV HAS run, the banner used to
          vanish entirely — taking the only path back to the CV surface from
          this screen with it (operator-reported 2026-06-11). It now flips to
          a slim completed variant whose button opens the results. */}
      {cvCompleted === false && (
        <div className="cv-nudge-banner" role="status">
          <span className="cv-nudge-icon">&#9432;</span>
          <div className="cv-nudge-content">
            <strong>Contextual Validity has not been run for this journey.</strong>
            <p>
              Bridge AI insights will be more precise with problem declaration
              and data-fit assessment.
            </p>
          </div>
          {onNavigateToCV && (
            <button className="btn cv-nudge-action" onClick={onNavigateToCV}>
              Run Contextual Validity &rarr;
            </button>
          )}
        </div>
      )}
      {cvCompleted === true && onNavigateToCV && (
        <div className="cv-nudge-banner cv-nudge-banner--done" role="status">
          <span className="cv-nudge-icon">&#10003;</span>
          <div className="cv-nudge-content">
            <strong>Contextual Validity has been assessed for this journey.</strong>
          </div>
          <button className="btn cv-nudge-action" onClick={onNavigateToCV}>
            View Contextual Validity &rarr;
          </button>
        </div>
      )}

      <section className="zone-actionable-intelligence">
        <div className="zone-header zone-header--with-action">
          <div className="zone-header-left">
            <h2 className="zone-title">Actionable Intelligence</h2>
            <span className="zone-subtitle">Prioritized Recommendations with Verified ROI</span>
          </div>
          <div className="zone-header-right">
            <div className="pistoia-badge">
              <span className="badge-icon">✓</span>
              <span>Pistoia Alliance</span>
            </div>
            <button
              className="btn-synthesize"
              onClick={handleGenerateClick}
              disabled={generating}
            >
              {generating ? (
                <>
                  <span className="btn-spinner" />
                  Synthesizing...
                </>
              ) : hasInsights ? (
                <>🔄 Regenerate Insights</>
              ) : (
                <>✨ Generate Insights</>
              )}
            </button>
          </div>
        </div>

        {/* Layer 1: Assessment Value Banner */}
        <AssessmentValueBanner assessmentValue={bridgeIntelligence?.assessment_value} />

        {/* Tab Navigation */}
        <div className="action-tabs">
          <button
            className={`action-tab ${activeTab === 'summary' ? 'active' : ''}`}
            onClick={() => setActiveTab('summary')}
          >
            Executive Briefing
          </button>
          <button
            className={`action-tab ${activeTab === 'actions' ? 'active' : ''}`}
            onClick={() => setActiveTab('actions')}
          >
            Priority Actions
          </button>
          <button
            className={`action-tab ${activeTab === 'value-drivers' ? 'active' : ''} ${!hasInsights ? 'disabled' : ''}`}
            onClick={() => hasInsights && setActiveTab('value-drivers')}
            disabled={!hasInsights}
          >
            Value Drivers
          </button>
          <button
            className={`action-tab ${activeTab === 'causal' ? 'active' : ''}`}
            onClick={() => setActiveTab('causal')}
            title="Deep causal analysis - understand root causes and interventions"
          >
            🔬 Causal Analysis
          </button>
        </div>

        {/* Tab Content */}
        <div className="action-content">
          {generating && (
            <div className="generating-overlay" role="status">
              <div className="generating-spinner" />
              <p>Synthesizing insights...</p>
              <p className="generating-note">This may take 2-3 minutes</p>
            </div>
          )}

          {activeTab === 'summary' && (
            <ExecutiveSummaryTab summary={bridgeIntelligence?.executive_summary} />
          )}

          {activeTab === 'actions' && (
            bridgeIntelligence?.recommendations?.length ? (
              <>
                <BridgeRecommendations
                  recommendations={bridgeIntelligence.recommendations}
                  journey_id={journeyId}
                />
                {/* Organic handoff: smart recommendations → the full, phased Action
                    Plan. The launch point the v2.0 tab-bar removal dropped. */}
                <div className="value-drivers-cta">
                  <p className="cta-text">
                    Turn these recommendations into a sequenced plan. The Action Plan organizes
                    them into Quick Wins, Foundational work, and Strategic Bets with ROI and a timeline.
                  </p>
                  <button
                    className="btn-action-plan"
                    onClick={() => navigate(`/journey/${journeyId}?tab=action-plan`)}
                  >
                    📋 View Full Action Plan
                  </button>
                </div>
              </>
            ) : (
              <div className="actions-empty">
                <div className="empty-icon">🎯</div>
                <h3>Priority Actions</h3>
                <p>Generate AI insights to receive prioritized recommendations based on station findings.</p>
              </div>
            )
          )}

          {activeTab === 'value-drivers' && (
            <ValueDriversTab
              prismData={prismData}
              hasInsights={hasInsights}
              onNavigateToActionPlan={() => navigate(`/journey/${journeyId}?tab=action-plan`)}
            />
          )}

          {activeTab === 'causal' && (
            <CausalInsightsPanel
              journeyId={journeyId}
              onAnalysisComplete={(insights) => {
                console.log('[StrategicDashboard] Causal analysis complete:', insights.root_causes.length, 'root causes');
              }}
            />
          )}
        </div>
      </section>

      {/* ================================================================
          CONTEXT MODAL - Business context for AI synthesis
          ================================================================ */}
      {showContextModal && (
        <div className="context-modal-overlay" onClick={() => setShowContextModal(false)}>
          <div className="context-modal" onClick={(e) => e.stopPropagation()}>
            <div className="context-modal-header">
              <h3>Add Business Context</h3>
              <button
                className="close-modal-btn"
                onClick={() => setShowContextModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="context-modal-body">
              <label htmlFor="journey-context">
                <strong>Business Context</strong> (optional)
              </label>
              <p className="context-help-text">
                Provide business context to get more targeted recommendations. For example:
                acquisition scenario, compliance audit, ML/AI initiative, regulatory deadlines, etc.
              </p>
              <textarea
                id="journey-context"
                value={userContext}
                onChange={(e) => setUserContext(e.target.value)}
                placeholder="e.g., We're preparing for a data quality audit ahead of an FDA submission in Q2. The primary concern is demonstrating traceability and completeness of clinical trial data..."
                className="context-textarea"
                rows={5}
              />
            </div>
            <div className="context-modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setShowContextModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-generate"
                onClick={handleSynthesizeInsights}
              >
                {hasInsights ? '🔄 Regenerate' : '✨ Generate'} Insights
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StrategicIntelligenceDashboard;
